import { curry } from 'ramda';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { compose } from 'recompose';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css, keyframes } from 'styled-components';
import type { PropsOf } from '@peloton/react';
import history from '@peloton/redux/history';
import BreakpointPropSwitch from '@peloton/responsive/BreakpointPropSwitch';
import { defaultTransition, ellipsis, media } from '@peloton/styles';
import type { Omit } from '@peloton/types';
import { isDefined } from '@peloton/types';
import type { FeatureModule } from '@engage/api-v2';
import { getRelevantSongInfo } from '@engage/class-groups';
import { isAudioClass } from '@engage/classes';
import { toCloudinaryUrl } from '@engage/cloudinary/toCloudinaryUrl';
import { gray1, gray3, slate3, superRedA11y, white } from '@engage/colors';
import { If } from '@engage/conditional-render';
import { Source } from '@engage/filters';
import type { ClassPreview } from '@engage/library';
import { spaces } from '@engage/styles';
import { label12, label12Caps, label16 } from '@engage/typography';
import BulletSeparator from '@engage/typography/BulletSeparator';
import { BookmarkButton } from '@members/bookmark/BookmarkButton';
import { button1Styles } from '@members/buttons';
import { useClasses, Classes } from '@members/copy';
import { useOnDemandButtonHandler } from '@members/global-button-handlers/useOnDemandButtonHandler';
import PlayIcon from '@members/icons/Play';
import TakenCheck from '@members/icons/TakenCheck';
import { ClassInfoTag } from '@members/labels';
import AudioLabel from '@members/labels/AudioLabel';
import DifficultyLabel, {
  Size as DifficultyLabelSize,
} from '@members/labels/DifficultyLabel';
import { LocalizedAbbreviatedLong } from '@members/localization';
import { toOnDemandModalQuery } from '@members/on-demand-modal/helpers';
import { FIRST_CLASS_REC_SOURCE } from '@members/pg-homescreen/analytics';
import { SearchPageContext } from '@members/pg-search/context';
import { toVideoOnDemandUrl } from '@members/pg-video/urls';
import { ClassLabelSection } from '@members/tiering/components/ClassLabelSection';
import { useIsFreemiumClass } from '@members/tiering/hooks/useIsFreemiumClass';
import { useTieringClassLabel } from '@members/tiering/hooks/useTieringClassLabel';
import type { ClassAvailabilityProps } from '@members/tiering/models';
import { Friends } from './Friends';
import { RelevantSongInfoContainer, RelevantSongInfoRow } from './RelevantSongInfoRow';
import { VideoPreview } from './VideoPreview';

const thumbnail = require('./thumbnail_overlay.png');

/**
 * Must be wrapped in a container with `position: relative` in order
 * for the bookmark button to appear in the correct position
 */
export const ClassPreviewCard: React.FC<
  React.PropsWithChildren<ClassPreviewCardProps>
> = props => {
  const [isVideoReady, setIsVideoReady] = React.useState(false);
  const takenAltText = useClasses(Classes.ClassTakenText);
  const relevantSongInfo = getRelevantSongInfo(props);
  const hasRelevantClassInfo = isDefined(relevantSongInfo);
  const isOnHomescreen = window.location.pathname.includes('home');
  const playButtonLabel = useClasses(Classes.PlayClass);
  const currentStartTime = useMemo(() => new Date().toISOString(), []);
  const [hoveringOnCard, setHoveringOnCard] = useState(false);
  const showClassPreview =
    !(isOnHomescreen && props.freeForLimitedTime) && hoveringOnCard;
  const {
    pageState: { status },
  } = React.useContext(SearchPageContext);

  const source = React.useMemo(() => {
    if (props.source) {
      return props.source;
    } else if (status === 'showResults') {
      return Source.ClassSearch;
    } else if (status === 'viewingArtist') {
      return Source.ArtistSearch;
    } else if (isOnHomescreen) {
      return Source.Featured;
    } else {
      return Source.OnDemandLibrary;
    }
  }, [isOnHomescreen, props.source, status]);

  const handlePlayOnClick = () => history.push(toVideoOnDemandUrl(props.id), { source });

  const classAvailabilityProps = !!props?.contentAvailability
    ? ({
        contentAvailability: props.contentAvailability,
        freeForLimitedTime: props.freeForLimitedTime,
        isLimitedRide: props.isLimitedRide,
      } as ClassAvailabilityProps)
    : undefined;

  const { classTierLabel, labelStyles } = useTieringClassLabel(
    props.id,
    classAvailabilityProps,
  );
  const isFreemiumClass = useIsFreemiumClass(props.id, classAvailabilityProps);

  const {
    isDisabled,
    startHandler: onDemandStartHandler,
    backgroundStyles,
  } = useOnDemandButtonHandler(
    props.id,
    handlePlayOnClick,
    undefined,
    classAvailabilityProps,
  );

  return (
    <>
      <StyledLink
        to={{
          pathname: window.location.pathname,
          search: toOnDemandModalQuery(props.id, props.source),
          state: {
            source,
            listPosition: props.listPosition,
            featuredModule: props.classGroup?.name,
            rank: props.classGroup?.rankDigital,
            total: props.classGroup?.total,
          },
        }}
        onMouseOver={() => setHoveringOnCard(true)}
        onMouseLeave={() => setHoveringOnCard(false)}
      >
        <BackgroundImageContainer
          data-test-id="videoCell"
          backgroundImage={toCloudinaryUrl(props.imageUrl, {
            width: 512,
          })}
          hasRelevantClassInfo={hasRelevantClassInfo}
          height={props.height}
          width={props.width}
          shouldFadeOut={showClassPreview && isVideoReady}
        >
          {showClassPreview ? (
            <VideoPreview
              currentStartTime={currentStartTime}
              rideId={props.id}
              imageUrl={props.imageUrl}
              onReadyCallback={() => {
                setIsVideoReady(true);
              }}
              source={source}
            />
          ) : null}
          <ClassInfoIconBlock>
            {props.userHasTakenClass && !props.hideCheckmark ? (
              <StyledTakenCheck
                data-test-id="takenClassIcon"
                id={props.id}
                boundingBox
                title={takenAltText}
              />
            ) : null}
            <Friends num={props.totalFollowingWorkouts} />
          </ClassInfoIconBlock>
          <BasicInfoContainer hasPlayButton={props.hasPlayButton}>
            <StyledDifficultyLabel
              data-test-id="classDifficulty"
              level={props.difficultyLevel}
            />
            <Title data-test-id="videoCellTitle">{props.title}</Title>
            <Subtitle data-test-id="videoCellSubtitle">
              {props.subtitle1}
              <BulletSeparator />
              <ClassInfoTag
                fitnessDisciplineName={props.subtitle2}
                originLocale={props.originLocale}
              />
            </Subtitle>
            {isFreemiumClass && (
              <ClassLabelSection
                label={label12}
                labelStyles={labelStyles}
                margin={`${spaces.xSmall}px ${spaces.xxxSmall}px 0px 0px;`}
              >
                {classTierLabel}
              </ClassLabelSection>
            )}
            {props.scheduledStartTime &&
            !props.hideScheduledStartTime &&
            !isFreemiumClass ? (
              <AiredTime>
                <ClassLabelSection
                  label={label12}
                  labelStyles={labelStyles}
                  margin={`0px ${spaces.xxxSmall}px 0px 0px;`}
                >
                  {classTierLabel}
                </ClassLabelSection>
                <LocalizedAbbreviatedLong date={props.scheduledStartTime} />
              </AiredTime>
            ) : null}
            {isAudioClass(props.contentFormat) &&
            props.source != FIRST_CLASS_REC_SOURCE ? (
              <AudioLabelWrapper>
                <AudioLabel data-test-id="videoCellAudioTag" />
              </AudioLabelWrapper>
            ) : null}
          </BasicInfoContainer>
        </BackgroundImageContainer>
        <If condition={hasRelevantClassInfo}>
          <RelevantSongInfoRow relevantSongInfo={relevantSongInfo!} />
        </If>
      </StyledLink>
      {props.isOnHomescreen ? null : (
        <BookmarkButton
          isBookmarked={props.isBookmarked}
          filled={props.isBookmarked}
          onClick={handleClick(props.onBookmarkClick)}
        />
      )}
      {props.hasPlayButton ? (
        <PlayClassButton
          data-test-id="playIcon"
          aria-label={playButtonLabel}
          onClick={onDemandStartHandler}
          disabled={isDisabled}
          backgroundStyles={backgroundStyles}
        >
          <PlayIcon width={18} height={18} />
        </PlayClassButton>
      ) : null}
    </>
  );
};

const handleClick = curry(
  (onBookmarkClick: ClassPreviewCardProps['onBookmarkClick'], e: any) => {
    e.preventDefault();
    onBookmarkClick();
  },
);

const ClassInfoIconBlock = styled.div`
  position: absolute;
  display: flex;
  top: ${spaces.small}px;
  left: ${spaces.small}px;
`;

const StyledTakenCheck = styled(TakenCheck)`
  margin-right: ${spaces.xSmall}px;
  height: 26px;
  width: 26px;
  opacity: 0.8;
  ${media.desktop`
    height: 30px;
    width: 30px;
  `}
`;

const Fadeout = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;

  }
`;

const toFadeOutStyle = (shouldFadeOut: boolean) => {
  if (!shouldFadeOut) return '';

  return css`
    animation: ${Fadeout} 1s ease forwards;
  `;
};

export const BackgroundImageContainer = styled.div<{
  backgroundImage: string;
  hasRelevantClassInfo: boolean;
  height?: number;
  width?: number;
  shouldFadeOut?: boolean;
}>`
  display: block;
  position: relative;
  border-radius: 5px;
  border-bottom-left-radius: ${props => (props.hasRelevantClassInfo ? '0px' : '5px')};
  border-bottom-right-radius: ${props => (props.hasRelevantClassInfo ? '0px' : '5px')};
  padding-bottom: 56%; /* 16:9, i.e. 9 / 16 */
  z-index: 0;
  background-color: ${slate3};
  background-repeat: no-repeat;
  ${p => (p?.height ? `height: ${p.height}px;` : '')}
  ${p => (p?.width ? `width: ${p.width}px;` : '')}

  // Gradient Overlay
  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    right: 100%;
    top: 0;
    bottom: 100%;
    background-size: cover;
    z-index: -1;
    border-radius: 5px;
    opacity: 1;

    background-position: top center;
    background-size: cover;
    background-image: url('${props => props.backgroundImage}');

    ${({ shouldFadeOut }) => toFadeOutStyle(!!shouldFadeOut)}
  }

  // Gradient Overlay
  &::before {
    background-image: url('${thumbnail}');
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    right: 100%;
    top: 0;
    bottom: 100%;
    background-size: cover;
    z-index: 0;
    border-radius: 5px;
    opacity: 1;
  }

  // No touch devices
  @media (-moz-touch-enabled: 0),
    (pointer: fine),
    (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    filter: brightness(100%);
    ${defaultTransition('filter')};
  }
`;

const StyledLink = styled(Link)`
  ${BackgroundImageContainer} {
    filter: brightness(115%);
  }

  &:hover ${RelevantSongInfoContainer} {
    background-color: ${gray1};
  }
`;

const withPlayButtonStyles = css`
  width: 70%;
  max-height: 70%;
  overflow: hidden;
`;

const BasicInfoContainer = styled.div<{ hasPlayButton?: boolean }>`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  ${({ hasPlayButton }) => hasPlayButton && `${withPlayButtonStyles}`};

  ${media.desktop`
    bottom: 15px;
    left: 15px;
    right: 15px;
  `}
  color: ${white};
`;

export const Title = styled.h2`
  ${label16}
  max-height: ${spaces.xHuge}px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: initial;
  display: block;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const Subtitle = styled.p`
  ${ellipsis}
  ${label12Caps}
  margin-top: ${spaces.tiny}px;
`;

export const AiredTime = styled.div`
  display: flex;
  align-items: center;
  ${label12}
  color: ${gray3};
  text-transform: capitalize;
  margin-top: 9px;
`;

const AudioLabelWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const PlayClassButton = styled.button<{ backgroundStyles: FlattenSimpleInterpolation }>`
  ${props =>
    props.backgroundStyles ? props.backgroundStyles : `background-color: ${superRedA11y}`}
  ${button1Styles};
  position: absolute;
  bottom: 16px;
  right: 16px;
  padding: 0;
  line-height: 0;
  color: white;
  border-radius: 50%;
  svg {
    margin: 16px;
  }
`;
type DifficultyLabelProps = PropsOf<typeof DifficultyLabel>;

const propMap = {
  mobile: { size: DifficultyLabelSize.Small },
  tablet: { size: DifficultyLabelSize.Medium },
  desktopXLarge: { size: DifficultyLabelSize.Large },
};

const ResponsiveDifficultyLabel = compose<
  Pick<DifficultyLabelProps, 'size'>,
  Omit<DifficultyLabelProps, 'size'>
>(BreakpointPropSwitch<Pick<DifficultyLabelProps, 'size'>>(propMap))(DifficultyLabel);

export const StyledDifficultyLabel = styled(ResponsiveDifficultyLabel)`
  color: ${gray3};
`;

export type ClassPreviewCardProps = ClassPreview & {
  totalFollowingWorkouts: number;
  onBookmarkClick: (ride?: ClassPreview) => void;
  listPosition?: number;
  isOnHomescreen?: boolean;
  classGroup?: FeatureModule & { rankDigital: number };
  source?: string;
  hasPlayButton?: boolean;
  hideScheduledStartTime?: boolean;
  hideCheckmark?: boolean;
  height?: number;
  width?: number;
};
